import type {
  ViduV1Activity,
  ViduV1Creation,
  ViduV1ExploreDetailProReply,
  ViduV1GetInspirationIdsForSeoReply,
  ViduV1InspirationItem,
  ViduV1MediaAsset,
  ViduV1MediaAssetDetail,
  ViduV1MiniVideo,
  ViduV1MiniVideoDetail,
  ViduV1MyInspiration,
  ViduV1ShortFilm,
  ViduV1ShortFilmDetailPro,
  ViduV1Spotlight,
  ViduV1Tutorial,
  ViduV1TutorialDetail,
  ViduV1WorkAward,
  ViduV1WorkWithTask,
} from '@/app/services/vidu';
import type { WorkWithTask } from './activity';
import type { CreationWithTask } from './creation';

export type {
  ViduV1ExploreDetailProReply,
  ViduV1GetInspirationIdsForSeoReply,
  ViduV1InspirationItem,
  ViduV1MediaAsset,
  ViduV1ShortFilm,
  ViduV1Spotlight,
  ViduV1Tutorial,
  ViduV1WorkWithTask,
};

export type VoteType = ViduV1Creation['vote'];

export type ShortFilmType = ViduV1ShortFilmDetailPro & {
  traceId?: string;
  isActivity?: boolean;
  /** 关联的活动信息 */
  activity?: ViduV1Activity;
  ip_location?: string;
};

export type TutorialType = ViduV1TutorialDetail & {
  traceId?: string;
  ip_location?: string;
};
export type InspirationType = ViduV1MediaAssetDetail & {
  // 覆盖后端返回的 info，因为 creation 需要使用 task 内的 creations 字段进行合并
  info: ViduV1MediaAsset & {
    creation: ViduV1Creation;
    /** 奖项信息 */
    award?: ViduV1WorkAward;
  };
  traceId?: string;
  isActivity?: boolean;
  /** 是否图片灵感（基于详情接口的 type=image_asset 标记） */
  isImageAsset?: boolean;
  ip_location?: string;
  /** 关联的活动信息 */
  activity?: ViduV1Activity;
};

export type MiniVideoType = ViduV1MiniVideoDetail & {
  traceId?: string;
  ip_location?: string;
};

export type SelectedAsset =
  | {
      creation?: CreationWithTask;
      inspiration?: InspirationType;
      shortFilm?: ShortFilmType;
      tutorial?: TutorialType;
      miniVideo?: MiniVideoType;
    }
  | undefined;

export type FeedType = 'unspecified' | 'short_film' | 'media_asset' | 'tutorial' | 'image_asset';

export type ExploreTab = Exclude<FeedType, 'unspecified'>;

/** 个人作品/灵感列表项（用于 profile、FeedDetail 等） */
export type InspirationItem = ViduV1MyInspiration & {
  id?: string;
};

/**
 * 社区类型定义
 */
export enum CommunityEnum {
  ImageAsset = 'image_asset',
  MediaAsset = 'media_asset',
  ShortFilm = 'short_film',
  Tutorial = 'tutorial',
  PostReferences = 'share_element',
  MiniVideo = 'mini_video',
}

/**
 * 社区路由定义
 */
export enum ExploreTabRouterEnum {
  Inspiration = 'inspiration',
  ShortFilm = 'short-film',
  Tutorial = 'tutorial',
  PostReferences = 'reference',
  Activity = 'activity',
  Cases = 'cases',
}

/**
 * 首页路由定义
 */
export enum HomeTabRouterEnum {
  Recommend = 'recommend',
  Activity = 'activity',
}

/**
 * 首页推荐二级筛选 tab
 */
export enum RecommendSubTab {
  All = 'all',
  Inspiration = 'inspiration',
  ShortFilm = 'short_film',
  Tutorial = 'tutorial',
}

/**
 * 社区发现页资产定义
 */
export enum ExploreAssetTypeEnum {
  Inspiration = 'inspiration',
  ShortFilm = 'shortFilm',
  Tutorial = 'tutorial',
  MiniVideo = 'miniVideo',
}

// 更新社区互动状态参数
export type UpdateInteractionStatusParams = {
  id: string;
  obj_type: CommunityEnum;
  traceId?: string;
  voted?: boolean;
  voteType?: VoteType;
  collected?: boolean;
  collectType?: VoteType;
  count?: number;
  collect_count?: number;
};
export type UpdateInteractionStatusFn = (params: UpdateInteractionStatusParams) => void;

/** FeedDetail/社区相关组件的通用 Props（供 FeedDetail、CreationDetail 等公用组件使用） */
export type CommunityProps = {
  onRecreateStart?: () => void;
  onChangeEpisode?: (episodeId?: string) => void;
  source?: 'share' | 'inspiration' | 'others';
  onClose?: () => void;
  onVideoPlayEnd?: () => void;
  handleVote?: UpdateInteractionStatusFn;
  handleUnVote?: UpdateInteractionStatusFn;
  handleCollect?: UpdateInteractionStatusFn;
  handleUnCollect?: UpdateInteractionStatusFn;
};

/** 缩略图滚动列表 Props（FeedDetail 版本） */
export interface ThumbScrollListProps {
  /** 导航列表数据 */
  list?: (InspirationType | ShortFilmType | TutorialType | WorkWithTask | InspirationItem)[];
  /** 点击缩略图回调 */
  onThumbnailClick?: (id: string, traceId: string, index?: number) => void;
  /** 无限滚动回调 */
  onLoadMore?: () => void;
  /** 是否还有更多数据 */
  hasMore?: boolean;
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 自定义类名 */
  className?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 媒体区域引用，用于滚轮事件监听 */
  mediaAreaRef?: React.RefObject<HTMLElement | null>;
  /** 当前选中的 ID */
  currentId?: string;
}

/** FeedDetail 配置 */
export type FeedDetailConfig = CommunityProps & {
  thumbScrollListConfig?: ThumbScrollListProps;
};

/** 灵感资产列表（用于 Feed 等） */
export type NewShowCaseList = Array<
  ViduV1MyInspiration & {
    traceId?: string;
  }
>;

/** 展示案例列表（短片、灵感、小视频等） */
export type ShowCaseList = Array<
  (ViduV1MediaAsset | ViduV1ShortFilm | ViduV1MiniVideo) & { traceId?: string; detail?: string }
>;

/** Masonry 网格单元加载状态（用于 FeedList、PostReferencePanel 等） */
export type LoadingState = Record<number, boolean>;
